import { useMemo } from 'react';
import type { Episode } from '../types/episodes';
import Picture from './Picture';
import { Play } from 'lucide-react';

type EpisodesTeaserProps = {
  episodes: Episode[];
  onNavigateToEpisode: (slug: string) => void;
  onNavigateToAllEpisodes: () => void;
};

export default function EpisodesTeaser({ episodes, onNavigateToEpisode, onNavigateToAllEpisodes }: EpisodesTeaserProps) {
  const recentEpisodes = useMemo(() => episodes.slice(0, 4), [episodes]);

  return (
    <section id="episodes" className="py-20 bg-gradient-to-b from-black via-purple-900/20 to-black">
      <div className="container mx-auto px-4">
        <div className="flex justify-center mb-12">
          <h2 className="font-press-start text-2xl md:text-3xl text-black bg-[#ffed00] text-center py-2 px-4 inline-block">
            Waiting For Players
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {recentEpisodes.map((episode) => (
            <EpisodeCard
              key={episode.slug}
              episode={episode}
              onClick={() => onNavigateToEpisode(episode.slug)}
            />
          ))}
        </div>

        <div className="text-center">
          <button
            onClick={onNavigateToAllEpisodes}
            className="bg-[#ffed00] text-black px-8 py-4 font-bold text-lg hover-scale hover:bg-[#ffed00]/90 transition-colors"
          >
            Browse All Episodes
          </button>
        </div>
      </div>
    </section>
  );
}

function EpisodeCard({ episode, onClick }: { episode: Episode; onClick: () => void }) {
  const storageRoot = episode.storage_root;

  return (
    <div
      onClick={onClick}
      className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-600/50 rounded-lg overflow-hidden hover-scale cursor-pointer group"
    >
      <div className="relative h-48 overflow-hidden">
        {episode.game_thumbnail_460x215 ? (
          <Picture
            storageRoot={storageRoot}
            filename={episode.game_thumbnail_460x215}
            alt={episode.title}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <span className="text-gray-500">No Image</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Play className="text-yellow-400" size={48} />
        </div>
      </div>
      <div className="p-4">
        <div className="text-red-400 text-xs font-bold mb-1">{episode.podcast_number}</div>
        <h3 className="font-press-start text-xs text-yellow-400 mb-2 line-clamp-2 leading-relaxed">
          {episode.title}
        </h3>
        <p className="text-gray-300 text-sm line-clamp-2">
          {episode.summary?.substring(0, 100) || 'No summary available'}...
        </p>
      </div>
    </div>
  );
}
